import { useEffect, useRef } from "react";
import { bindAnimPause } from "./animPause";
import { LAND } from "./worldLand";

/*
 * Career globe — a slowly rotating orthographic earth on canvas: continents,
 * a pole-converging graticule, and traffic between the team's four points
 * (Budapest, São Paulo, Singapore, Sydney). They sit on opposite sides of the
 * planet, so the rotation is what lets each of them have its moment.
 */

interface Hub {
  lat: number;
  lon: number;
  label: string;
}

const HUBS: Hub[] = [
  { lat: 47.5, lon: 19.06, label: "BUDAPEST" },
  { lat: -22.91, lon: -43.17, label: "RIO" },
  { lat: 1.36, lon: 103.99, label: "SINGAPORE" },
  { lat: -33.94, lon: 151.18, label: "SYDNEY" },
  { lat: 25.79, lon: -80.29, label: "MIAMI" },
];

/* every pair — the team works as one network */
const ROUTES: [number, number][] = [
  [0, 1], [0, 2], [0, 3], [0, 4], [1, 2], [1, 3], [1, 4], [2, 3], [2, 4], [3, 4],
];

const D2R = Math.PI / 180;
const LAT0 = 18; /* viewing tilt */
const SAMPLES = 56;

type V3 = [number, number, number];

const vec = (lat: number, lon: number): V3 => {
  const f = lat * D2R, l = lon * D2R;
  return [Math.cos(f) * Math.cos(l), Math.cos(f) * Math.sin(l), Math.sin(f)];
};

/* great-circle samples in 3D, computed once — rotation is projection-only */
const gc = (a: Hub, b: Hub): V3[] => {
  const va = vec(a.lat, a.lon), vb = vec(b.lat, b.lon);
  const dot = Math.max(-1, Math.min(1, va[0] * vb[0] + va[1] * vb[1] + va[2] * vb[2]));
  const w = Math.acos(dot), sw = Math.sin(w);
  const out: V3[] = [];
  for (let i = 0; i <= SAMPLES; i++) {
    const t = i / SAMPLES;
    const k1 = Math.sin((1 - t) * w) / sw, k2 = Math.sin(t * w) / sw;
    out.push([
      k1 * va[0] + k2 * vb[0],
      k1 * va[1] + k2 * vb[1],
      k1 * va[2] + k2 * vb[2],
    ]);
  }
  return out;
};

const ROUTE_PTS: V3[][] = ROUTES.map(([a, b]) => gc(HUBS[a], HUBS[b]));
const HUB_V: V3[] = HUBS.map((h) => vec(h.lat, h.lon));

export default function CareerGlobeArt() {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const g = c.getContext("2d");
    if (!g) return;

    const CS = getComputedStyle(document.documentElement);
    const C = (n: string) => CS.getPropertyValue(n).trim();
    const CA = (n: string, a: number) => {
      let h = C(n).replace("#", "");
      if (h.length === 3) h = h.split("").map((x) => x + x).join("");
      const v = parseInt(h, 16);
      return `rgba(${(v >> 16) & 255},${(v >> 8) & 255},${v & 255},${a})`;
    };
    const T = { line: "--mist", route: "--cyan", hub: "--amber" };
    const RM = matchMedia("(prefers-reduced-motion: reduce)").matches;

    let W = 0, H = 0, D = 1, R = 1, cx = 0, cy = 0;
    const s0 = Math.sin(LAT0 * D2R), c0 = Math.cos(LAT0 * D2R);
    let sl = 0, cl = 1; /* sin/cos of the current rotation */

    /* rotate a fixed 3D point by the current spin, then tilt and project.
       Returns screen x, y and depth (z > 0 faces the viewer). */
    const P = (v: V3): [number, number, number] => {
      const x = v[0] * cl + v[1] * sl;
      const y = -v[0] * sl + v[1] * cl;
      const z = v[2];
      return [cx + R * y, cy - R * (c0 * z - s0 * x), s0 * z + c0 * x];
    };

    function strand(pts: V3[], close = false) {
      let on = false;
      g!.beginPath();
      const run = close ? pts.concat([pts[0]]) : pts;
      run.forEach((v) => {
        const [x, y, z] = P(v);
        if (z <= 0.01) { on = false; return; }
        if (!on) { g!.moveTo(x, y); on = true; } else g!.lineTo(x, y);
      });
      g!.stroke();
    }

    /* traffic: two dots per route, opposite directions */
    interface Dot { r: number; t: number; sp: number; fwd: boolean; }
    const AC: Dot[] = [];
    ROUTES.forEach((_, i) => {
      AC.push({ r: i, t: (i * 0.37) % 1, sp: 0.0028 + (i % 3) * 0.0011, fwd: true });
      AC.push({ r: i, t: (i * 0.53 + 0.5) % 1, sp: 0.0031 + ((i + 1) % 3) * 0.0009, fwd: false });
    });
    const at = (pts: V3[], t: number): V3 => {
      const f = Math.max(0, Math.min(1, t)) * (pts.length - 1), i = Math.floor(f), u = f - i;
      const a = pts[i], b = pts[Math.min(pts.length - 1, i + 1)];
      return [a[0] + (b[0] - a[0]) * u, a[1] + (b[1] - a[1]) * u, a[2] + (b[2] - a[2]) * u];
    };

    /* arrival ripples, pinned to a hub so they ride the rotation */
    interface Ripple { hub: number; age: number; }
    const ripples: Ripple[] = [];

    function size() {
      D = Math.min(devicePixelRatio || 1, 3);
      const r = c!.getBoundingClientRect();
      /* mid-reflow the rect can read 0 — committing that blanks the canvas
         for good, so wait for the next resize instead */
      if (r.width < 10 || r.height < 10) return;
      W = Math.max(1, r.width);
      H = Math.max(1, r.height);
      c!.width = W * D;
      c!.height = H * D;
      g!.setTransform(D, 0, 0, D, 0, 0);
      R = Math.min(W, H) * 0.42;
      cx = W / 2;
      cy = H / 2;
    }

    let tick = 0, rafLoop = 0, rafSize = 0;
    function frame() {
      tick++;
      /* one revolution ≈ 48 s */
      const lon0 = RM ? 60 : (tick * 0.125) % 360;
      sl = Math.sin(lon0 * D2R);
      cl = Math.cos(lon0 * D2R);

      g!.clearRect(0, 0, W, H);

      /* disc */
      g!.strokeStyle = CA(T.line, 0.5);
      g!.lineWidth = 1;
      g!.beginPath();
      g!.arc(cx, cy, R, 0, 7);
      g!.stroke();

      /* graticule — meridians pole to pole, parallels around */
      g!.strokeStyle = CA(T.line, 0.2);
      for (let lon = 0; lon < 360; lon += 30) {
        const pts: V3[] = [];
        for (let lat = -90; lat <= 90; lat += 6) pts.push(vec(lat, lon));
        strand(pts);
      }
      for (let lat = -60; lat <= 60; lat += 30) {
        const pts: V3[] = [];
        for (let lon = 0; lon <= 360; lon += 6) pts.push(vec(lat, lon));
        strand(pts);
      }

      /* continents — Natural Earth 110m, real coastlines */
      g!.strokeStyle = CA(T.line, 0.55);
      g!.lineWidth = 1;
      LAND.forEach((poly) => strand(poly.map(([lon, lat]) => vec(lat, lon)), true));

      /* routes */
      g!.strokeStyle = CA(T.route, 0.5);
      g!.lineWidth = 1;
      ROUTE_PTS.forEach((pts) => strand(pts));

      /* traffic with trails */
      AC.forEach((a) => {
        a.t += a.sp;
        if (a.t >= 1) {
          a.t = 0;
          ripples.push({ hub: ROUTES[a.r][a.fwd ? 1 : 0], age: 0 });
        }
        const pts = ROUTE_PTS[a.r];
        for (let k = 0; k < 6; k++) {
          const tt = a.t - k * 0.018;
          if (tt < 0) continue;
          const [x, y, z] = P(at(pts, a.fwd ? tt : 1 - tt));
          if (z <= 0.01) continue;
          g!.fillStyle = CA(T.route, (1 - k / 6) * 0.9);
          g!.beginPath();
          g!.arc(x, y, 2 * (1 - k / 10), 0, 7);
          g!.fill();
        }
      });

      /* arrival ripples */
      for (let i = ripples.length - 1; i >= 0; i--) {
        const rp = ripples[i];
        rp.age += 1;
        if (rp.age > 60) { ripples.splice(i, 1); continue; }
        const [x, y, z] = P(HUB_V[rp.hub]);
        if (z <= 0.01) continue;
        const u = rp.age / 60;
        g!.strokeStyle = CA(T.route, 0.6 * (1 - u));
        g!.lineWidth = 1;
        g!.beginPath();
        g!.arc(x, y, 3 + u * 14, 0, 7);
        g!.stroke();
      }

      /* the hubs breathe while facing us — unnamed, just points of pull */
      HUB_V.forEach((v, i) => {
        const [x, y, z] = P(v);
        if (z <= 0.01) return;
        const fade = Math.min(1, z * 4);
        const breathe = 0.5 + 0.5 * Math.sin(tick / 40 + i * 1.9);
        g!.fillStyle = CA(T.hub, fade);
        g!.beginPath();
        g!.arc(x, y, 2.6 + breathe * 0.9, 0, 7);
        g!.fill();
        g!.strokeStyle = CA(T.hub, 0.3 * (1 - breathe) * fade);
        g!.beginPath();
        g!.arc(x, y, 4 + breathe * 8, 0, 7);
        g!.stroke();
      });

      if (!RM) rafLoop = requestAnimationFrame(frame);
    }

    /* phone URL-bar show/hide fires height-only resizes; the figure box
       is width-driven there, and a rebuild blanks the canvas for a frame */
    let lastArtW = window.innerWidth;
    const phoneMQ = window.matchMedia("(max-width: 768px)");
    const onResize = () => {
      if (phoneMQ.matches && window.innerWidth === lastArtW) return;
      lastArtW = window.innerWidth;
      cancelAnimationFrame(rafSize);
      rafSize = requestAnimationFrame(() => {
        size();
        if (!RM) { cancelAnimationFrame(rafLoop); rafLoop = requestAnimationFrame(frame); }
        else frame();
      });
    };
    window.addEventListener("resize", onResize);
    /* the canvas can mount before its column settles its final size —
       track the element itself, not just the window */
    const ro = new ResizeObserver(onResize);
    ro.observe(c);

    size();
    frame(); /* with reduced motion this paints one static view */
    requestAnimationFrame(() => c.classList.add("ready"));

    const unbind = RM ? null : bindAnimPause(
      c,
      () => { cancelAnimationFrame(rafLoop); rafLoop = requestAnimationFrame(frame); },
      () => cancelAnimationFrame(rafLoop)
    );

    return () => {
      cancelAnimationFrame(rafLoop);
      if (unbind) unbind();
      cancelAnimationFrame(rafSize);
      ro.disconnect();
      window.removeEventListener("resize", onResize);
    };
  }, []);

  return <canvas ref={ref} aria-hidden="true" />;
}
